"use client";

import { useTranslations } from "next-intl";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { Check } from "lucide-react";
import { PLAN_LINKS, PLAN_LINK_ATTRIBUTES } from "@/config/plan-links";

interface PricingSectionProps {
  transparent?: boolean;
}

export function PricingSection({ transparent = false }: PricingSectionProps) {
  const t = useTranslations("Home.pricing");
  const titleAnimation = useScrollAnimation({ threshold: 0.2 });
  const cardsAnimation = useScrollAnimation({ threshold: 0.2 });

  return (
    <section
      id="pricing"
      className="py-24 px-4 sm:px-8 relative overflow-hidden"
    >
      {!transparent && (
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/60 to-black"></div>
      )}
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Título y Descripción */}
        <div
          ref={titleAnimation.ref}
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            titleAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 font-[family-name:var(--font-big-shoulders)] uppercase leading-tight">
            {t("title")}
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t("description")}
          </p>
        </div>

        {/* Plan Anual */}
        <div
          ref={cardsAnimation.ref}
          className={`max-w-lg mx-auto transition-all duration-1000 ease-out ${
            cardsAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <div className="bg-gray-900/70 border-2 border-[#E91E63] rounded-lg p-8 relative flex flex-col">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2 font-[family-name:var(--font-big-shoulders)] uppercase">
                {t("annual.title")}
              </h3>
              <div className="text-4xl font-bold text-white mb-2">
                {t("annual.price")}
              </div>
              <p className="text-[#E91E63] font-semibold text-sm">
                {t("annual.subtitle")}
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-grow">
              {t
                .raw("annual.benefits")
                .map((benefit: string, index: number) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[#E91E63] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{benefit}</span>
                  </li>
                ))}
            </ul>

            <div className="mt-auto">
              <p className="text-gray-400 text-sm mb-6 text-center">
                {t("annual.additional")}
              </p>

              <a
                href={PLAN_LINKS.ANNUAL}
                {...PLAN_LINK_ATTRIBUTES}
                className="w-full bg-[#E91E63] hover:bg-[#D81B60] text-white py-3 px-6 font-bold transition-all duration-300 hover:scale-105 block text-center"
              >
                {t("annual.cta")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
